import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { MediaAssetInput } from '../../components/settings/MediaAssetPicker';
import { mediaUrl } from '../../components/media/MediaAssetLibrary';
import CrmModal from '../../components/ui/CrmModal';
import NotificationSnackbar from '../../components/ui/NotificationSnackbar';
import { useExperienceStorefront } from '../../components/website/StorefrontExperience';
import { fetchStorefrontFooter, saveStorefrontFooter, fetchStorefrontMenus } from '../../services/storefrontAdminService';
import { useStorefrontCms } from '../../services/fusionBoxCmsService';

const emptySocial = { platform: '', url: '', is_active: true };
const initialFooterForm = {
  enabled: true,
  logo_url: '',
  tagline: '',
  show_contact_email: true,
  show_contact_phone: true,
  show_address: false,
  bottom_bar_enabled: true,
  copyright_text: '',
  legal_menu_id: '',
  menu_slots: [],
};

export default function FooterEditor() {
  const storefront = useExperienceStorefront();
  const logoRef = useRef();
  const cms = useStorefrontCms();
  const [menus, setMenus] = useState([]);
  const [form, setForm] = useState(initialFooterForm);
  const [socials, setSocials] = useState([]);
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialForm, setSocialForm] = useState(emptySocial);
  const [note, setNote] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setNote({ open: true, message, severity });

  const load = async () => {
    try {
      const [footer, menuResult] = await Promise.all([
        fetchStorefrontFooter(storefront.code),
        fetchStorefrontMenus(storefront.code),
      ]);
      setForm({
        ...initialFooterForm,
        ...(footer.settings || {}),
        bottom_bar_enabled: footer.settings?.bottom_bar_enabled === undefined ? true : !!footer.settings.bottom_bar_enabled,
        legal_menu_id: footer.settings?.legal_menu_id || '',
        menu_slots: footer.menu_slots || [],
      });
      setSocials(footer.socials || []);
      setMenus((menuResult.menus || []).filter((menu) => menu.display_area === 'FOOTER' && menu.is_active));
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to load footer', 'error');
    }
  };

  useEffect(() => {
    if (storefront?.code) load();
  }, [storefront?.code]);

  const patch = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const patchSlot = (index, key, value) => setForm((current) => ({
    ...current,
    menu_slots: current.menu_slots.map((slot, slotIndex) => (slotIndex === index ? { ...slot, [key]: value } : slot)),
  }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        menu_slots: (form.menu_slots || []).map((slot, index) => ({
          ...slot,
          slot_key: slot.slot_key || `column_${index + 1}`,
        })),
      };
      await saveStorefrontFooter(storefront.code, payload);
      notify('Footer saved');
      load();
    } catch (error) {
      notify(error.response?.data?.details || 'Save failed', 'error');
    }
  };

  const addSocial = async () => {
    if (!socialForm.platform.trim() || !socialForm.url.trim()) {
      notify('Platform and URL are required', 'error');
      return;
    }
    try {
      await cms.createFooterSocial({
        platform: socialForm.platform.trim(),
        url: socialForm.url.trim(),
        is_active: socialForm.is_active ? 1 : 0,
        display_order: socials.length,
      });
      setSocialOpen(false);
      setSocialForm(emptySocial);
      await load();
      notify('Social link added');
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to add social link', 'error');
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Footer</Typography>
          <Typography color="text.secondary">
            Configure the footer shell, menu columns, social links and bottom bar. Links themselves are managed in Menus.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}>Save Footer</Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card><CardContent>
            <FormControlLabel
              control={<Switch checked={form.enabled !== false} onChange={(event) => patch('enabled', event.target.checked)} />}
              label="Footer enabled"
            />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card><CardContent>
            <Typography variant="h6" fontWeight={800}>Brand & tagline</Typography>
            <MediaAssetInput ref={logoRef} onSelect={(asset) => patch('logo_url', asset.url)} />
            {form.logo_url ? <Box component="img" src={form.logo_url.startsWith('/images/') ? form.logo_url : mediaUrl(form.logo_url)} alt="Storefront logo preview" sx={{ display: 'block', maxWidth: 220, maxHeight: 90, mt: 2, objectFit: 'contain' }} /> : null}
            <Button sx={{ my: 1 }} variant="outlined" onClick={() => logoRef.current?.click()}>Choose Logo</Button>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Tagline"
              value={form.tagline || ''}
              onChange={(event) => patch('tagline', event.target.value)}
            />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card><CardContent>
            <Typography variant="h6" fontWeight={800}>Contact presentation</Typography>
            <Stack>
              <FormControlLabel control={<Switch checked={form.show_contact_email !== false} onChange={(event) => patch('show_contact_email', event.target.checked)} />} label="Show contact email" />
              <FormControlLabel control={<Switch checked={form.show_contact_phone !== false} onChange={(event) => patch('show_contact_phone', event.target.checked)} />} label="Show contact phone" />
              <FormControlLabel control={<Switch checked={!!form.show_address} onChange={(event) => patch('show_address', event.target.checked)} />} label="Show address" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Values resolve from the storefront's Brand / Legal Entity context; they are not duplicated here.
            </Typography>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight={800}>Menu columns</Typography>
                <Typography variant="body2" color="text.secondary">Assign Footer menus. Column identifiers are generated automatically.</Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() => patch('menu_slots', [...(form.menu_slots || []), { label: '', menu_id: '' }])}
              >
                Add Column
              </Button>
            </Stack>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {(form.menu_slots || []).map((slot, index) => (
                <Grid container spacing={1} alignItems="center" key={slot.id || index}>
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Heading" value={slot.label || ''} onChange={(event) => patchSlot(index, 'label', event.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select fullWidth size="small" label="Menu" value={slot.menu_id || ''} onChange={(event) => patchSlot(index, 'menu_id', event.target.value)}>
                      {menus.map((menu) => <MenuItem key={menu.id} value={menu.id}>{menu.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton color="error" onClick={() => patch('menu_slots', form.menu_slots.filter((_slot, itemIndex) => itemIndex !== index))}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Stack>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight={800}>Social links</Typography>
                <Typography variant="body2" color="text.secondary">Social links stay directly owned by the footer.</Typography>
              </Box>
              <Button startIcon={<AddIcon />} onClick={() => { setSocialForm(emptySocial); setSocialOpen(true); }}>Add Social Link</Button>
            </Stack>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {socials.map((social) => (
                <Stack key={social.id} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                  <Typography sx={{ minWidth: 140, fontWeight: 700 }}>{social.platform}</Typography>
                  <Typography sx={{ flex: 1, wordBreak: 'break-all' }} color="text.secondary">{social.url}</Typography>
                  <IconButton
                    color="error"
                    onClick={async () => {
                      try {
                        await cms.deleteFooterSocial(social.id);
                        await load();
                        notify('Social link deleted');
                      } catch {
                        notify('Failed to delete social link', 'error');
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={800}>Bottom bar</Typography>
                <Typography variant="body2" color="text.secondary">Configure the final strip below the main footer.</Typography>
              </Box>
              <Switch checked={form.bottom_bar_enabled !== false} onChange={(event) => patch('bottom_bar_enabled', event.target.checked)} />
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  label="Copyright text"
                  value={form.copyright_text || ''}
                  disabled={form.bottom_bar_enabled === false}
                  onChange={(event) => patch('copyright_text', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  select
                  fullWidth
                  label="Legal menu"
                  value={form.legal_menu_id || ''}
                  disabled={form.bottom_bar_enabled === false}
                  onChange={(event) => patch('legal_menu_id', event.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {menus.map((menu) => <MenuItem key={menu.id} value={menu.id}>{menu.name}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </CardContent></Card>
        </Grid>
      </Grid>

      <CrmModal
        open={socialOpen}
        onClose={() => setSocialOpen(false)}
        title="Add Social Link"
        subtitle="Add a social profile to this storefront footer."
        size="sm"
        footer={(
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setSocialOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={addSocial}>Add Social Link</Button>
          </Stack>
        )}
      >
        <Stack spacing={2}>
          <FormControlLabel
            control={<Switch checked={socialForm.is_active !== false} onChange={(event) => setSocialForm((current) => ({ ...current, is_active: event.target.checked }))} />}
            label="Active"
          />
          <TextField fullWidth label="Platform" value={socialForm.platform} onChange={(event) => setSocialForm((current) => ({ ...current, platform: event.target.value }))} />
          <TextField fullWidth label="URL" value={socialForm.url} onChange={(event) => setSocialForm((current) => ({ ...current, url: event.target.value }))} />
        </Stack>
      </CrmModal>

      <NotificationSnackbar open={note.open} message={note.message} severity={note.severity} onClose={() => setNote((current) => ({ ...current, open: false }))} />
    </Box>
  );
}
